import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Alert,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';
import { ArrowBack, CalendarMonth, Today, InfoOutlined } from '@mui/icons-material';
import {
    ComposedChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import { vortxColors, vortxTypography, useThemeMode } from '@vortx/shared-ui';
import { MONTH_OPTIONS } from '../hooks/useColdOptimization';

const fmt = (v) => v != null ? `$${v.toFixed(2)}` : '--';
const fmtKw = (v) => v != null ? `${v.toFixed(1)} kW` : '--';
const fmtPct = (v) => v != null ? `${v.toFixed(1)}%` : '--';

const cardStyle = { bgcolor: 'rgba(33, 150, 243, 0.08)' };

/**
 * ResultsChart - Visualize cold storage optimization results
 *
 * Supports three display modes:
 *   1. Annual summary (type: 'annual', no month selected) — summary table with 12 months
 *   2. Monthly detail (month selected) — daily-aggregated load/temp charts
 *   3. Daily detail (month + day selected) — quarter-hour view
 *
 * Also supports legacy single-month results (no type field).
 */
export default function ResultsChart({ results, zones = [] }) {
    const { isRetro } = useThemeMode();
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);

    if (!results) {
        return (
            <Alert severity="info">
                Configure facility and run optimization to see results.
            </Alert>
        );
    }

    const isAnnual = results?.type === 'annual';

    // For annual results with no month detail data (summary-only from Firestore reload)
    const isSummaryOnly = isAnnual && (!results.months || Object.values(results.months).every(m => !m?.load_curve_kw));

    // Build zone name/bounds maps
    const zoneNameMap = {};
    const zoneBounds = {};
    for (const zone of zones) {
        if (zone.id) {
            zoneNameMap[zone.id] = zone.name || zone.id;
            zoneBounds[zone.id] = {
                tempMin: zone.temp_min,
                tempMax: zone.temp_max,
                target: zone.target_temp,
            };
        }
    }

    // ========================================
    // LEVEL 1: Annual Summary
    // ========================================
    if (isAnnual && !selectedMonth) {
        const { annual_summary } = results;
        const hasSummary = !!annual_summary;

        return (
            <Box>
                {/* Annual cost cards */}
                {hasSummary && (
                    <>
                        {annual_summary.total_savings > 0 && (
                            <Card sx={{ mb: 2, bgcolor: 'rgba(46, 125, 50, 0.12)', border: '1px solid rgba(46, 125, 50, 0.3)' }}>
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" sx={{ color: '#81c784' }}>
                                                Annual Savings vs. Baseline
                                            </Typography>
                                            <Typography variant="h5" sx={{ color: '#66bb6a', fontFamily: vortxTypography.mono, fontWeight: 700 }}>
                                                {fmt(annual_summary.total_savings)}
                                                <Typography component="span" variant="body2" sx={{ color: '#81c784', ml: 1 }}>
                                                    ({fmtPct(annual_summary.savings_pct)})
                                                </Typography>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Typography variant="caption" sx={{ color: '#81c784' }}>Total Cost</Typography>
                                            <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.total_cost)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Typography variant="caption" sx={{ color: '#81c784' }}>Baseline Cost</Typography>
                                            <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.baseline_total_cost)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Typography variant="caption" sx={{ color: '#81c784' }}>Peak Demand</Typography>
                                            <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                                {fmtKw(annual_summary.peak_demand_kw)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Typography variant="caption" sx={{ color: '#81c784' }}>Tariff</Typography>
                                            <Typography variant="body2" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono, fontSize: '0.8rem' }}>
                                                {annual_summary.tariff_used || '--'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {/* Summary cards when no savings */}
                        {!annual_summary.total_savings && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>Annual Total</Typography>
                                            <Typography variant="h6" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.total_cost)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>Energy Cost</Typography>
                                            <Typography variant="h6" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.energy_cost)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>Demand Charges</Typography>
                                            <Typography variant="h6" sx={{ color: '#f57c00', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.demand_cost)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>Peak Demand</Typography>
                                            <Typography variant="h6" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                                {fmtKw(annual_summary.peak_demand_kw)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}

                {/* Summary-only notice — shown when detail data is not in memory */}
                {isSummaryOnly && (
                    <Alert
                        severity="info"
                        icon={<InfoOutlined />}
                        sx={{
                            mb: 2,
                            bgcolor: 'rgba(33, 150, 243, 0.08)',
                            border: '1px solid rgba(33, 150, 243, 0.25)',
                            '& .MuiAlert-icon': { color: '#42a5f5' },
                        }}
                    >
                        <Typography variant="body2" sx={{ color: vortxColors.white }}>
                            Showing saved summary from a previous run.
                            Click <strong>Run Full Year Optimization</strong> above to view detailed monthly charts and daily drill-downs.
                        </Typography>
                    </Alert>
                )}

                {/* Monthly summary table */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ color: vortxColors.white }}>
                                Monthly Breakdown
                            </Typography>
                            {!isSummaryOnly && (
                                <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                    Click a month to see daily detail
                                </Typography>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Month</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Energy</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Demand</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Total</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Peak kW</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Savings</TableCell>
                                        <TableCell align="right" sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Savings %</TableCell>
                                        <TableCell sx={{ color: vortxColors.stone, borderBottom: '2px solid #2196F3' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {MONTH_OPTIONS.map((month) => {
                                        const r = results.months?.[month.value];
                                        const ms = results.monthly_summaries?.[month.value];
                                        // Use full result if available, fall back to summary
                                        const data = r?.status === 'optimal' ? r : ms;
                                        const isOptimal = r?.status === 'optimal' || ms?.status === 'optimal';
                                        const hasDetail = r?.load_curve_kw?.length > 0;
                                        const totalCost = data ? (data.energy_cost || 0) + (data.demand_cost || 0) : null;
                                        const savingsVal = r?.savings?.total_cost || ms?.savings_total || 0;
                                        const savingsPct = r?.savings?.total_cost_pct || ms?.savings_pct || 0;

                                        return (
                                            <TableRow
                                                key={month.value}
                                                hover={hasDetail}
                                                onClick={() => hasDetail ? setSelectedMonth(month.value) : null}
                                                sx={{
                                                    cursor: hasDetail ? 'pointer' : 'default',
                                                    opacity: isSummaryOnly ? 0.7 : 1,
                                                    '&:hover': hasDetail ? { bgcolor: 'rgba(33, 150, 243, 0.08)' } : {},
                                                }}
                                            >
                                                <TableCell sx={{ color: vortxColors.white, fontWeight: 500 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {hasDetail && <CalendarMonth sx={{ fontSize: 16, color: '#2196F3' }} />}
                                                        {month.label}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                                    {isOptimal ? fmt(data.energy_cost) : '--'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#f57c00', fontFamily: vortxTypography.mono }}>
                                                    {isOptimal ? fmt(data.demand_cost) : '--'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono, fontWeight: 600 }}>
                                                    {isOptimal ? fmt(totalCost) : '--'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                                    {isOptimal ? fmtKw(data.peak_demand_kw) : '--'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: savingsVal > 0 ? '#66bb6a' : vortxColors.stone, fontFamily: vortxTypography.mono }}>
                                                    {isOptimal && savingsVal > 0 ? fmt(savingsVal) : '--'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: savingsPct > 0 ? '#66bb6a' : vortxColors.stone, fontFamily: vortxTypography.mono }}>
                                                    {isOptimal && savingsPct > 0 ? fmtPct(savingsPct) : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    {r?.status === 'optimal' ? (
                                                        <Chip label="Optimal" size="small" sx={{ bgcolor: 'rgba(46,125,50,0.2)', color: '#66bb6a', height: 22, fontSize: '0.7rem' }} />
                                                    ) : r?.status === 'infeasible' ? (
                                                        <Chip label="Infeasible" size="small" sx={{ bgcolor: 'rgba(211,47,47,0.2)', color: '#ff6b6b', height: 22, fontSize: '0.7rem' }} />
                                                    ) : r?.status === 'error' ? (
                                                        <Chip label="Error" size="small" sx={{ bgcolor: 'rgba(211,47,47,0.2)', color: '#ff6b6b', height: 22, fontSize: '0.7rem' }} />
                                                    ) : ms ? (
                                                        <Chip label="Summary" size="small" sx={{ bgcolor: 'rgba(33,150,243,0.2)', color: '#42a5f5', height: 22, fontSize: '0.7rem' }} />
                                                    ) : (
                                                        <Chip label="--" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: vortxColors.stone, height: 22, fontSize: '0.7rem' }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {/* Annual Total Row */}
                                    {hasSummary && (
                                        <TableRow sx={{ '& td': { borderTop: '2px solid #2196F3', fontWeight: 700 } }}>
                                            <TableCell sx={{ color: vortxColors.white, fontWeight: 700 }}>Year Total</TableCell>
                                            <TableCell align="right" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.energy_cost)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#f57c00', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.demand_cost)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono }}>
                                                {fmt(annual_summary.total_cost)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                                {fmtKw(annual_summary.peak_demand_kw)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: annual_summary.total_savings > 0 ? '#66bb6a' : vortxColors.stone, fontFamily: vortxTypography.mono }}>
                                                {annual_summary.total_savings > 0 ? fmt(annual_summary.total_savings) : '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: annual_summary.savings_pct > 0 ? '#66bb6a' : vortxColors.stone, fontFamily: vortxTypography.mono }}>
                                                {annual_summary.savings_pct > 0 ? fmtPct(annual_summary.savings_pct) : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${annual_summary.months_solved}/12`}
                                                    size="small"
                                                    sx={{ bgcolor: 'rgba(33,150,243,0.2)', color: '#42a5f5', height: 22, fontSize: '0.7rem' }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // ========================================
    // LEVEL 2 & 3: Monthly / Daily detail
    // ========================================

    // Determine the data to chart
    let monthData;
    let periodLabel;

    if (isAnnual && selectedMonth) {
        monthData = results.months[selectedMonth];
        const monthOpt = MONTH_OPTIONS.find(m => m.value === selectedMonth);
        periodLabel = monthOpt?.label || selectedMonth;
    } else if (!isAnnual) {
        // Legacy single-month result
        monthData = results;
        periodLabel = 'Monthly';
    }

    if (!monthData) {
        return (
            <Box>
                <Button startIcon={<ArrowBack />} onClick={() => { setSelectedMonth(null); setSelectedDay(null); }} sx={{ color: '#2196F3', mb: 2 }}>
                    Back to Annual Summary
                </Button>
                <Alert severity="warning">No data available for this month.</Alert>
            </Box>
        );
    }

    if (monthData.status === 'infeasible') {
        return (
            <Box>
                {isAnnual && (
                    <Button startIcon={<ArrowBack />} onClick={() => { setSelectedMonth(null); setSelectedDay(null); }} sx={{ color: '#2196F3', mb: 2 }}>
                        Back to Annual Summary
                    </Button>
                )}
                <Alert severity="warning">
                    {monthData.message || 'Optimization is infeasible for this month.'}
                </Alert>
            </Box>
        );
    }

    if (monthData.status === 'error') {
        return (
            <Box>
                {isAnnual && (
                    <Button startIcon={<ArrowBack />} onClick={() => { setSelectedMonth(null); setSelectedDay(null); }} sx={{ color: '#2196F3', mb: 2 }}>
                        Back to Annual Summary
                    </Button>
                )}
                <Alert severity="error">
                    Optimization failed: {monthData.message || 'Unknown error'}
                </Alert>
            </Box>
        );
    }

    const {
        load_curve_kw = [],
        objective_value,
        energy_cost,
        demand_cost,
        peak_demand_kw,
        results_by_zone = {},
        tariff_used,
        baseline,
        savings,
    } = monthData;

    const baselineLoad = baseline?.load_curve_kw || [];
    const baselineByZone = baseline?.results_by_zone || {};
    const stepsPerDay = 96;
    const zoneIds = Object.keys(results_by_zone);
    const hasTemps = zoneIds.some(id => results_by_zone[id]?.temperature_trajectory);

    // Compute temp bounds for reference lines
    let globalTempMin = null;
    let globalTempMax = null;
    let globalTarget = null;
    for (const zoneId of zoneIds) {
        const bounds = zoneBounds[zoneId];
        if (bounds) {
            if (globalTempMin === null || bounds.tempMin > globalTempMin) globalTempMin = bounds.tempMin;
            if (globalTempMax === null || bounds.tempMax < globalTempMax) globalTempMax = bounds.tempMax;
            if (globalTarget === null) globalTarget = bounds.target;
        }
    }

    // Distinct color sets per zone
    const zoneColorSets = [
        { avg: '#2196F3', min: '#5c6bc0', max: '#ef6c00' },
        { avg: '#66bb6a', min: '#26a69a', max: '#ff7043' },
        { avg: '#ab47bc', min: '#7e57c2', max: '#ffa726' },
        { avg: '#ec407a', min: '#c62828', max: '#ffd54f' },
        { avg: '#42a5f5', min: '#7986cb', max: '#ff8a65' },
    ];

    const numDays = Math.ceil(load_curve_kw.length / stepsPerDay);

    // ========================================
    // LEVEL 3: Daily quarter-hour detail
    // ========================================
    if (selectedDay != null) {
        const dayStart = selectedDay * stepsPerDay;
        const dayEnd = Math.min(dayStart + stepsPerDay, load_curve_kw.length);
        const dayLoad = load_curve_kw.slice(dayStart, dayEnd);
        const dayBaseline = baselineLoad.slice(dayStart, dayEnd);

        const dayChartData = dayLoad.map((load, i) => {
            const point = {
                time: `${Math.floor(i / 4)}:${String((i % 4) * 15).padStart(2, '0')}`,
                load_kw: Math.round(load * 100) / 100,
            };
            if (dayBaseline[i] != null) {
                point.baseline_kw = Math.round(dayBaseline[i] * 100) / 100;
            }
            return point;
        });

        // Day temperature data
        let dayTempData = [];
        if (hasTemps) {
            const numSteps = dayEnd - dayStart;
            for (let i = 0; i < numSteps; i++) {
                const point = { time: `${Math.floor(i / 4)}:${String((i % 4) * 15).padStart(2, '0')}` };
                for (const zoneId of zoneIds) {
                    const temps = results_by_zone[zoneId]?.temperature_trajectory;
                    if (temps && temps[dayStart + i] != null) {
                        point[zoneId] = Math.round(temps[dayStart + i] * 10) / 10;
                    }
                    const blTemps = baselineByZone[zoneId]?.temperature_trajectory;
                    if (blTemps && blTemps[dayStart + i] != null) {
                        point[`${zoneId}_baseline`] = Math.round(blTemps[dayStart + i] * 10) / 10;
                    }
                }
                dayTempData.push(point);
            }
        }

        const dayPeak = Math.max(...dayLoad);
        const dayKwh = dayLoad.reduce((sum, kw) => sum + kw * 0.25, 0);

        return (
            <Box>
                {/* Breadcrumb navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {isAnnual && (
                        <Button
                            size="small"
                            startIcon={<ArrowBack />}
                            onClick={() => { setSelectedMonth(null); setSelectedDay(null); }}
                            sx={{ color: '#2196F3' }}
                        >
                            Annual
                        </Button>
                    )}
                    <Typography sx={{ color: vortxColors.stone }}>/</Typography>
                    <Button
                        size="small"
                        startIcon={<CalendarMonth />}
                        onClick={() => setSelectedDay(null)}
                        sx={{ color: '#2196F3' }}
                    >
                        {periodLabel}
                    </Button>
                    <Typography sx={{ color: vortxColors.stone }}>/</Typography>
                    <Chip
                        icon={<Today />}
                        label={`Day ${selectedDay + 1}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#2196F3' }}
                    />
                </Box>

                {/* Day summary cards */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="caption" sx={{ color: vortxColors.stone }}>Daily Energy</Typography>
                                <Typography variant="h6" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono }}>
                                    {dayKwh.toFixed(1)} kWh
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="caption" sx={{ color: vortxColors.stone }}>Peak Demand</Typography>
                                <Typography variant="h6" sx={{ color: '#f57c00', fontFamily: vortxTypography.mono }}>
                                    {dayPeak.toFixed(1)} kW
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={cardStyle}>
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="caption" sx={{ color: vortxColors.stone }}>Duty Cycle</Typography>
                                <Typography variant="h6" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                    {((dayLoad.filter(kw => kw > 0).length / dayLoad.length) * 100).toFixed(0)}%
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Quarter-hour load chart */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: vortxColors.white }}>
                            Electrical Load — {periodLabel}, Day {selectedDay + 1}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={dayChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="time" tick={{ fill: vortxColors.stone, fontSize: 10 }} interval={3} />
                                <YAxis
                                    yAxisId="left"
                                    label={{ value: 'kW', angle: -90, position: 'insideLeft', fill: vortxColors.stone }}
                                    tick={{ fill: vortxColors.stone }}
                                />
                                <Tooltip
                                    formatter={(value, name) => {
                                        const labels = { load_kw: ['Optimized', 'kW'], baseline_kw: ['Baseline', 'kW'] };
                                        const [label, unit] = labels[name] || [name, ''];
                                        return [`${value} ${unit}`, label];
                                    }}
                                    contentStyle={{ backgroundColor: isRetro ? '#242424' : '#fff', border: '1px solid rgba(33,150,243,0.3)' }}
                                />
                                <Bar yAxisId="left" dataKey="load_kw" fill="#2196F3" name="load_kw" />
                                {dayBaseline.length > 0 && (
                                    <Line yAxisId="left" type="stepAfter" dataKey="baseline_kw" stroke="#ff7043" name="baseline_kw" dot={false} strokeWidth={2} strokeDasharray="6 4" />
                                )}
                                <Legend formatter={(v) => ({ load_kw: 'Optimized (kW)', baseline_kw: 'Baseline (kW)' }[v] || v)} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Quarter-hour temperature chart */}
                {hasTemps && dayTempData.length > 0 && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: vortxColors.white }}>
                                Zone Temperatures — {periodLabel}, Day {selectedDay + 1}
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={dayTempData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                    <XAxis dataKey="time" tick={{ fill: vortxColors.stone, fontSize: 10 }} interval={3} />
                                    <YAxis
                                        label={{ value: '°F', angle: -90, position: 'insideLeft', fill: vortxColors.stone }}
                                        tick={{ fill: vortxColors.stone }}
                                        domain={['auto', 'auto']}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => {
                                            const cleanName = name.replace(/_baseline$/, '');
                                            const zoneName = zoneNameMap[cleanName] || cleanName;
                                            if (name.endsWith('_baseline')) return [`${value}°F`, `${zoneName} (baseline)`];
                                            return [`${value}°F`, zoneName];
                                        }}
                                        contentStyle={{ backgroundColor: isRetro ? '#242424' : '#fff', border: '1px solid rgba(33,150,243,0.3)' }}
                                    />
                                    {globalTempMin != null && (
                                        <ReferenceLine y={globalTempMin} stroke="#ff6b6b" strokeDasharray="6 4" strokeWidth={1.5}
                                            label={{ value: `Max ${globalTempMin}°F`, fill: '#ff6b6b', fontSize: 11, position: 'right' }} />
                                    )}
                                    {globalTempMax != null && (
                                        <ReferenceLine y={globalTempMax} stroke="#ff6b6b" strokeDasharray="6 4" strokeWidth={1.5}
                                            label={{ value: `Min ${globalTempMax}°F`, fill: '#ff6b6b', fontSize: 11, position: 'right' }} />
                                    )}
                                    {globalTarget != null && (
                                        <ReferenceLine y={globalTarget} stroke="rgba(255,255,255,0.3)" strokeDasharray="3 3" strokeWidth={1}
                                            label={{ value: `Target ${globalTarget}°F`, fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                                    )}
                                    {zoneIds.map((zoneId, idx) => {
                                        const colors = zoneColorSets[idx % zoneColorSets.length];
                                        return (
                                            <React.Fragment key={zoneId}>
                                                <Line type="monotone" dataKey={zoneId} stroke={colors.avg} name={zoneId} dot={false} strokeWidth={2.5} />
                                                {baselineByZone[zoneId] && (
                                                    <Line type="monotone" dataKey={`${zoneId}_baseline`} stroke={colors.avg} name={`${zoneId}_baseline`}
                                                        dot={false} strokeWidth={1.5} strokeDasharray="8 4" opacity={0.5} />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    <Legend formatter={(value) => {
                                        const cleanName = value.replace(/_baseline$/, '');
                                        const zoneName = zoneNameMap[cleanName] || cleanName;
                                        if (value.endsWith('_baseline')) return `${zoneName} (baseline)`;
                                        return zoneName;
                                    }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}
            </Box>
        );
    }

    // ========================================
    // LEVEL 2: Monthly detail (daily aggregated)
    // ========================================

    // Build daily-aggregated chart data
    const chartData = Array.from({ length: numDays }, (_, day) => {
        const start = day * stepsPerDay;
        const end = Math.min(start + stepsPerDay, load_curve_kw.length);
        const daySlice = load_curve_kw.slice(start, end);
        const point = {
            time: `Day ${day + 1}`,
            day,
            kwh: Math.round(daySlice.reduce((sum, kw) => sum + kw * 0.25, 0) * 10) / 10,
            peak_kw: Math.round(Math.max(...daySlice) * 10) / 10,
        };
        if (baselineLoad.length > 0) {
            const blSlice = baselineLoad.slice(start, end);
            point.baseline_kwh = Math.round(blSlice.reduce((sum, kw) => sum + kw * 0.25, 0) * 10) / 10;
        }
        return point;
    });

    // Build daily temperature data
    let tempChartData = [];
    if (hasTemps) {
        const numTimesteps = results_by_zone[zoneIds[0]]?.temperature_trajectory?.length || 0;
        for (let day = 0; day < numDays; day++) {
            const start = day * stepsPerDay;
            const end = Math.min(start + stepsPerDay, numTimesteps);
            const point = { time: `Day ${day + 1}`, day };
            for (const zoneId of zoneIds) {
                const temps = results_by_zone[zoneId]?.temperature_trajectory;
                if (temps) {
                    const dayTemps = temps.slice(start, end);
                    if (dayTemps.length > 0) {
                        point[zoneId] = Math.round((dayTemps.reduce((s, t) => s + t, 0) / dayTemps.length) * 10) / 10;
                        point[`${zoneId}_min`] = Math.round(Math.min(...dayTemps) * 10) / 10;
                        point[`${zoneId}_max`] = Math.round(Math.max(...dayTemps) * 10) / 10;
                    }
                }
                const blTemps = baselineByZone[zoneId]?.temperature_trajectory;
                if (blTemps) {
                    const dayBlTemps = blTemps.slice(start, end);
                    if (dayBlTemps.length > 0) {
                        point[`${zoneId}_baseline`] = Math.round((dayBlTemps.reduce((s, t) => s + t, 0) / dayBlTemps.length) * 10) / 10;
                    }
                }
            }
            tempChartData.push(point);
        }
    }

    // Compute energy metrics
    const totalKwh = load_curve_kw.reduce((sum, kw) => sum + kw * 0.25, 0);
    const dutyCycle = load_curve_kw.filter(kw => kw > 0).length / (load_curve_kw.length || 1);
    const totalHours = load_curve_kw.length * 0.25;

    return (
        <Box>
            {/* Breadcrumb navigation */}
            {isAnnual && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBack />}
                        onClick={() => { setSelectedMonth(null); setSelectedDay(null); }}
                        sx={{ color: '#2196F3' }}
                    >
                        Annual Summary
                    </Button>
                    <Typography sx={{ color: vortxColors.stone }}>/</Typography>
                    <Chip
                        icon={<CalendarMonth />}
                        label={periodLabel}
                        size="small"
                        sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#2196F3' }}
                    />
                </Box>
            )}

            {/* Savings Banner */}
            {savings && savings.total_cost > 0 && (
                <Card sx={{ mb: 2, bgcolor: 'rgba(46, 125, 50, 0.12)', border: '1px solid rgba(46, 125, 50, 0.3)' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" sx={{ color: '#81c784' }}>
                                    Savings vs. Thermostat Baseline
                                </Typography>
                                <Typography variant="h5" sx={{ color: '#66bb6a', fontFamily: vortxTypography.mono, fontWeight: 700 }}>
                                    ${savings.total_cost.toFixed(2)}
                                    <Typography component="span" variant="body2" sx={{ color: '#81c784', ml: 1 }}>
                                        ({savings.total_cost_pct.toFixed(1)}%)
                                    </Typography>
                                </Typography>
                            </Grid>
                            <Grid item xs={4} sm={3}>
                                <Typography variant="caption" sx={{ color: '#81c784' }}>Energy Savings</Typography>
                                <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                    ${savings.energy_cost.toFixed(2)}
                                </Typography>
                            </Grid>
                            <Grid item xs={4} sm={3}>
                                <Typography variant="caption" sx={{ color: '#81c784' }}>Demand Savings</Typography>
                                <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                    ${savings.demand_cost.toFixed(2)}
                                </Typography>
                            </Grid>
                            <Grid item xs={4} sm={3}>
                                <Typography variant="caption" sx={{ color: '#81c784' }}>Peak Reduction</Typography>
                                <Typography variant="body1" sx={{ color: '#a5d6a7', fontFamily: vortxTypography.mono }}>
                                    {savings.peak_demand_reduction_kw.toFixed(1)} kW
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Cost Breakdown */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={cardStyle}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Energy Cost ({periodLabel})
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#2196F3', fontFamily: vortxTypography.mono }}>
                                ${energy_cost != null ? energy_cost.toFixed(2) : (objective_value != null ? objective_value.toFixed(2) : '--')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                {baseline ? `vs. $${baseline.energy_cost.toFixed(2)} baseline` : `${totalKwh.toFixed(0)} kWh consumed`}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={cardStyle}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Demand Charge (monthly)
                            </Typography>
                            <Typography variant="h6" sx={{ color: demand_cost > 0 ? '#f57c00' : vortxColors.stone, fontFamily: vortxTypography.mono }}>
                                ${demand_cost != null ? demand_cost.toFixed(2) : '0.00'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                {baseline ? `vs. $${baseline.demand_cost.toFixed(2)} baseline` : (peak_demand_kw != null ? `${peak_demand_kw.toFixed(1)} kW peak` : '--')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={cardStyle}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Compressor Duty
                            </Typography>
                            <Typography variant="h6" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono }}>
                                {(dutyCycle * 100).toFixed(0)}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                {(dutyCycle * totalHours).toFixed(1)}h of {totalHours}h running
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={cardStyle}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Tariff
                            </Typography>
                            <Typography variant="body1" sx={{ color: vortxColors.white, fontFamily: vortxTypography.mono, fontSize: '0.85rem', mt: 0.5 }}>
                                {tariff_used || '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                {zoneIds.length} zone{zoneIds.length !== 1 ? 's' : ''} optimized
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Load Curve (daily aggregated) - clickable bars */}
            {chartData.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ color: vortxColors.white }}>
                                Electrical Load ({periodLabel})
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                Click a day bar to see quarter-hour detail
                            </Typography>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={chartData} onClick={(e) => {
                                if (e?.activePayload?.[0]?.payload?.day != null) {
                                    setSelectedDay(e.activePayload[0].payload.day);
                                }
                            }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis
                                    dataKey="time"
                                    label={{ value: 'Day', position: 'insideBottom', offset: -5, fill: vortxColors.stone }}
                                    tick={{ fill: vortxColors.stone, fontSize: 11 }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    yAxisId="left"
                                    label={{ value: 'kWh', angle: -90, position: 'insideLeft', fill: vortxColors.stone }}
                                    tick={{ fill: vortxColors.stone }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    domain={[0, 'auto']}
                                    label={{ value: 'Peak kW', angle: 90, position: 'insideRight', fill: '#f57c00', fontSize: 12 }}
                                    tick={{ fill: '#f57c00', fontSize: 11 }}
                                    axisLine={{ stroke: '#f57c00' }}
                                    tickLine={{ stroke: '#f57c00' }}
                                />
                                <Tooltip
                                    formatter={(value, name) => {
                                        const labels = {
                                            kwh: ['Daily Energy', 'kWh'],
                                            baseline_kwh: ['Baseline Energy', 'kWh'],
                                            peak_kw: ['Peak Demand', 'kW'],
                                        };
                                        const [label, unit] = labels[name] || [name, ''];
                                        return [`${value} ${unit}`, label];
                                    }}
                                    contentStyle={{ backgroundColor: isRetro ? '#242424' : '#fff', border: '1px solid rgba(33,150,243,0.3)' }}
                                />
                                <Bar yAxisId="left" dataKey="kwh" fill="#2196F3" name="kwh" cursor="pointer" />
                                {baselineLoad.length > 0 && (
                                    <Line yAxisId="left" type="monotone" dataKey="baseline_kwh" stroke="#ff7043" name="baseline_kwh" dot={false} strokeWidth={2} strokeDasharray="6 4" />
                                )}
                                <Bar yAxisId="right" dataKey="peak_kw" fill="#f57c00" name="peak_kw" cursor="pointer" />
                                <Legend formatter={(v) => ({
                                    kwh: 'Optimized Energy (kWh)',
                                    baseline_kwh: 'Baseline Energy (kWh)',
                                    peak_kw: 'Peak Demand (kW)',
                                }[v] || v)} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            {/* Temperature Trajectories (daily aggregated) */}
            {hasTemps && tempChartData.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: vortxColors.white }}>
                            Zone Temperatures ({periodLabel})
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={tempChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis
                                    dataKey="time"
                                    label={{ value: 'Day', position: 'insideBottom', offset: -5, fill: vortxColors.stone }}
                                    tick={{ fill: vortxColors.stone, fontSize: 11 }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    label={{ value: '°F', angle: -90, position: 'insideLeft', fill: vortxColors.stone }}
                                    tick={{ fill: vortxColors.stone }}
                                    domain={['auto', 'auto']}
                                />
                                <Tooltip
                                    formatter={(value, name) => {
                                        const cleanName = name.replace(/_min$/, '').replace(/_max$/, '').replace(/_baseline$/, '');
                                        const zoneName = zoneNameMap[cleanName] || cleanName;
                                        if (name.endsWith('_baseline')) return [`${value}°F`, `${zoneName} (baseline)`];
                                        if (name.endsWith('_min')) return [`${value}°F`, `${zoneName} (daily low)`];
                                        if (name.endsWith('_max')) return [`${value}°F`, `${zoneName} (daily high)`];
                                        return [`${value}°F`, `${zoneName} (avg)`];
                                    }}
                                    contentStyle={{ backgroundColor: isRetro ? '#242424' : '#fff', border: '1px solid rgba(33,150,243,0.3)' }}
                                />
                                <Legend formatter={(value) => {
                                    const cleanName = value.replace(/_min$/, '').replace(/_max$/, '').replace(/_baseline$/, '');
                                    const zoneName = zoneNameMap[cleanName] || cleanName;
                                    if (value.endsWith('_baseline')) return `${zoneName} (baseline)`;
                                    if (value.endsWith('_min')) return `${zoneName} (daily low)`;
                                    if (value.endsWith('_max')) return `${zoneName} (daily high)`;
                                    return `${zoneName} (avg)`;
                                }} />
                                {globalTempMin != null && (
                                    <ReferenceLine y={globalTempMin} stroke="#ff6b6b" strokeDasharray="6 4" strokeWidth={1.5}
                                        label={{ value: `Max ${globalTempMin}°F`, fill: '#ff6b6b', fontSize: 11, position: 'right' }} />
                                )}
                                {globalTempMax != null && (
                                    <ReferenceLine y={globalTempMax} stroke="#ff6b6b" strokeDasharray="6 4" strokeWidth={1.5}
                                        label={{ value: `Min ${globalTempMax}°F`, fill: '#ff6b6b', fontSize: 11, position: 'right' }} />
                                )}
                                {globalTarget != null && (
                                    <ReferenceLine y={globalTarget} stroke="rgba(255,255,255,0.3)" strokeDasharray="3 3" strokeWidth={1}
                                        label={{ value: `Target ${globalTarget}°F`, fill: 'rgba(255,255,255,0.4)', fontSize: 10, position: 'right' }} />
                                )}
                                {zoneIds.map((zoneId, idx) => {
                                    const colors = zoneColorSets[idx % zoneColorSets.length];
                                    return (
                                        <React.Fragment key={zoneId}>
                                            <Line type="monotone" dataKey={zoneId} stroke={colors.avg} name={zoneId} dot={false} strokeWidth={2.5} />
                                            {baselineByZone[zoneId] && (
                                                <Line type="monotone" dataKey={`${zoneId}_baseline`} stroke={colors.avg} name={`${zoneId}_baseline`}
                                                    dot={false} strokeWidth={1.5} strokeDasharray="8 4" opacity={0.5} />
                                            )}
                                            <Line type="monotone" dataKey={`${zoneId}_min`} stroke={colors.min} name={`${zoneId}_min`}
                                                dot={false} strokeWidth={1.5} strokeDasharray="6 3" />
                                            <Line type="monotone" dataKey={`${zoneId}_max`} stroke={colors.max} name={`${zoneId}_max`}
                                                dot={false} strokeWidth={1.5} strokeDasharray="3 3" />
                                        </React.Fragment>
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
